import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { SortableContext } from '@dnd-kit/sortable'
import { CONSTANT } from '@salescore/client-base'
import type { ViewConfigFilterNodeForJoinOn, ViewConfigPropertyFilter } from '@salescore/core'
import { Button, Form, Row } from 'antd'
import { Array, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useMemo, useState } from 'react'

import type { NodeWithModel } from '../common/nodeWithModel'
import type { FilterFormItemState, ViewConfigFilterNodeForForm } from './common'
import { collectAllSortableIds, getFrameRect, resolveDragContext } from './filterDnd'
import {
  assignIds,
  type FilterTreeNodeId,
  moveGroup,
  moveLeaf,
  stripIds,
  updateGroupById,
  type ViewConfigFilterNodeForFormWithId,
} from './filterTreeNode'
import { initialState, ViewFilterNodeFormItem } from './ViewFilterNodeFormItem'

/**
 * SortableContext 用の no-op strategy。常に null を返して、非アクティブ要素を一切 transform しない。
 * （アクティブ要素は DragOverlay 不使用時、dnd-kit の生 transform で実体のまま追従する）
 * ネスト構造に対して verticalListSortingStrategy を使うと矩形の重なりで表示が崩れるため、それを回避する。
 */
const noopSortingStrategy = (): null => null

/** 青線（ドロップ位置インジケータ）の viewport 座標。width は「入る階層の枠」に一致させる。 */
interface DropIndicatorPosition {
  y: number
  left: number
  width: number
}

/** ドラッグ状態。青線の位置(indicator)は「ドラッグ中」のときだけ持てる（位置とライフサイクルを1つに統合）。 */
type DragState = { isDragging: false } | { isDragging: true; indicator: DropIndicatorPosition | null }

interface ViewFilterTreeFormProperties {
  filterTree: ViewConfigFilterNodeForForm | undefined
  propertiesWithNode: NodeWithModel[]
  isAdvancedMode: boolean
  cacheKey?: string
  inNestedForm?: boolean // XXX: このコンポーネント内でFormを使っているが、親でもFormを使っていると表示が崩れる。これを防ぐため、フォーム内で使う時はこの引数で指定する
  onAfterFinish?: (filterNode: ViewConfigFilterNodeForJoinOn | undefined) => void
  onChange?: (filterNode: ViewConfigFilterNodeForJoinOn | undefined, option?: { asInitialization?: boolean }) => void
  showWarning?: boolean
}

/**
 * cacheKey を key に渡して内部コンポーネントを remount させ、外部入力 (filterTree) 変化時の state 初期化を行う。
 * prop 変化での state リセットに useEffect を使わない React 推奨パターン。
 */
export const ViewFilterTreeForm = ({ cacheKey, ...rest }: ViewFilterTreeFormProperties): ReactElement => (
  <ViewFilterTreeFormContent key={cacheKey} {...rest} />
)

const ViewFilterTreeFormContent = ({
  filterTree,
  propertiesWithNode,
  isAdvancedMode,
  inNestedForm,
  onAfterFinish,
  onChange,
  showWarning,
}: Omit<ViewFilterTreeFormProperties, 'cacheKey'>): ReactElement => {
  const [rootNode, setRootNodeRaw] = useState<ViewConfigFilterNodeForFormWithId>(() =>
    assignIds(filterTree ?? initialState),
  )
  const [dragState, setDragState] = useState<DragState>({ isDragging: false })

  /**
   * 青線（インジケータ）位置の更新（ドラッグ中のみ）
   * - ドラッグ中でなければ位置を更新しない
   * - ドロップ後に遅れて届いた onDragMove はここで弾かれ、previous（isDragging: false）をそのまま返すので青線は再表示されない。
   */
  const updateIndicator = (indicator: DropIndicatorPosition | null): void => {
    setDragState((previous) => (previous.isDragging ? { isDragging: true, indicator } : previous))
  }

  /** id付きツリーを差し替えつつ onChange に永続化用ツリーを通知する。 */
  const updateRoot = (next: ViewConfigFilterNodeForFormWithId, option?: { asInitialization?: boolean }): void => {
    setRootNodeRaw(next)
    if (onChange !== undefined) {
      onChange(pipe(next, stripIds, convertFilterNodeStateToFilterNode), option)
    }
  }

  const onFinish = (): void => {
    const filterTree = pipe(rootNode, stripIds, convertFilterNodeStateToFilterNode)
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: validationの表示をちゃんとやりたいが、いったん後回し
    onAfterFinish?.(filterTree)
  }

  // DnD：リーフ／グループ共通のセンサ
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 3 } }))

  const onDragStart = (): void => {
    // 新しいドラッグ開始。位置未確定（indicator: null）。前回の残留も無い。
    setDragState({ isDragging: true, indicator: null })
  }

  const onDragEnd = (event: DragEndEvent): void => {
    setDragState({ isDragging: false })
    const context = resolveDragContext(event, rootNode)
    if (context === undefined) return
    const { activeId, activeKind, source, slot } = context
    // 元の位置に落としたら（同一親で index が元の位置）動かさない
    if (slot.parentId === source.parentId && slot.index === source.index) return

    // index は「自分を除いた配列」基準。補正不要で moveLeaf/moveGroup の remove→insert と整合。
    const targetIndex = slot.index
    if (activeKind === 'leaf') {
      updateRoot(moveLeaf({ root: rootNode, leafId: activeId, targetParentId: slot.parentId, targetIndex }))
      return
    }
    updateRoot(moveGroup({ root: rootNode, groupId: activeId, targetParentId: slot.parentId, targetIndex }))
  }

  /** ドラッグ中、ポインタ位置に対応する挿入スロットの位置にインジケータ（横線）を出す。 */
  const onDragMove = (event: DragMoveEvent): void => {
    const context = resolveDragContext(event, rootNode)
    if (context === undefined) {
      updateIndicator(null)
      return
    }
    const { source, slot } = context
    // 元の位置（自分の直前/直後）に居る間は、青線を「掴む前の元の位置」に固定表示する。
    // active の現在矩形（ポインタ追従）を使うと元位置でもヌルヌル動いてしまうため、initial 矩形を使う。
    const isNoMove = slot.parentId === source.parentId && slot.index === source.index
    if (isNoMove) {
      const initialRect = event.active.rect.current.initial
      const sourceFrame = getFrameRect(source.parentId)
      if (initialRect !== null && sourceFrame !== undefined) {
        // 元の位置では常に「対象の上（前）」に固定表示して、上下2パターンに割れないようにする。
        updateIndicator({ y: initialRect.top, left: sourceFrame.left, width: sourceFrame.width })
        return
      }
    }
    updateIndicator({ y: slot.y, left: slot.frameLeft, width: slot.frameWidth })
  }

  /** 任意の groupId の子としてリーフ／子グループ／論理演算子を差し替える。 */
  const updateGroup = (parameters: {
    groupId: FilterTreeNodeId
    transform: (node: ViewConfigFilterNodeForFormWithId) => ViewConfigFilterNodeForFormWithId
    option?: { asInitialization?: boolean }
  }): void => {
    const { groupId, transform, option } = parameters
    const next = updateGroupById(rootNode, groupId, transform)
    updateRoot(next, option)
  }

  // tree 全体をフラット化した id 列。ルートに単一の SortableContext を置く。
  const flattenedIds = useMemo(() => collectAllSortableIds(rootNode), [rootNode])

  return (
    <Form layout="vertical">
      {/* ドロップ先は over（衝突結果）ではなく findTargetSlot（ポインタ位置）で決める。*/}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter} // ドラッグ中の挙動維持のため意図的に残している
        modifiers={[restrictToFirstScrollableAncestor]}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onDragCancel={(): void => {
          setDragState({ isDragging: false })
        }}
      >
        {/* strategy は no-op。非アクティブ要素を transform で動かさず表示崩れを防ぐ。
            DragOverlay を使わないため、アクティブ要素は dnd-kit の生 transform で実体のまま追従する。 */}
        <SortableContext items={flattenedIds} strategy={noopSortingStrategy}>
          <ViewFilterNodeFormItem
            inNestedForm={inNestedForm}
            isAdvancedMode={isAdvancedMode}
            isRoot
            filterNodeState={rootNode}
            propertiesWithNode={propertiesWithNode}
            updateGroup={updateGroup}
            showWarning={showWarning}
          />
        </SortableContext>
        {/* ドロップ位置インジケータ：青線。横幅は「入る階層の枠」に一致させる（グループ内ならその枠幅）。 */}
        {dragState.isDragging && isNotNullish(dragState.indicator) && (
          <div
            className="pointer-events-none fixed z-[1000] h-0.5 rounded-[1px]"
            style={{
              top: dragState.indicator.y,
              left: dragState.indicator.left,
              width: dragState.indicator.width,
              backgroundColor: CONSTANT.colors.primaryColor,
            }}
          />
        )}
      </DndContext>
      {onAfterFinish !== undefined && (
        <Row justify="end">
          <Button
            onClick={() => {
              onFinish()
            }}
          >
            {t(`適用`)}
          </Button>
        </Row>
      )}
    </Form>
  )
}

// 必須項目が埋められていなかったら消してから返す
function convertFilterNodeStateToFilterNode(
  state: ViewConfigFilterNodeForForm,
): ViewConfigFilterNodeForJoinOn | undefined {
  const leafs = Array.flatMapNullishOr(state.leafs, (leaf) => validateFilterState(leaf))
  const converted = {
    logicalOperator: state.logicalOperator,
    leafs,
    children: Array.flatMapNullishOr(state.children, (child) => convertFilterNodeStateToFilterNode(child)),
  }

  if (converted.leafs.length === 0 && converted.children.length === 0) {
    return undefined
  }

  return converted
}

function validateFilterState(x: FilterFormItemState): ViewConfigPropertyFilter | undefined {
  if (x.filterType === undefined || x.property === undefined) {
    return undefined
  }

  return {
    type: 'property',
    property: x.property,
    filterType: x.filterType,
    filterValue: x.filterValue,
    filterDateTimeType: x.filterDateTimeType,
    option: x.option,
  }
}

export function applyAdvancedSettingsFlag<T>(isAdvanced: boolean, filterTree?: T) {
  if (filterTree === undefined || isAdvanced) {
    return filterTree
  }
  return { ...filterTree, logicalOperator: 'and', children: [] }
}
